import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ImageUpload } from "@/components/common/ImageUpload";
import { api, apiError } from "@/lib/api";

const L = ({ children }) => <label className="mb-1.5 block text-sm font-medium text-slate-700">{children}</label>;

function GroupPicker({ title, groups, selected, onChange, testPrefix }) {
  if (!groups.length) return null;
  const flip = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div>
      <L>{title}</L>
      <div className="flex flex-wrap gap-2">
        {groups.map((g) => (
          <label key={g.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
            <Checkbox checked={selected.includes(g.id)} onCheckedChange={() => flip(g.id)} data-testid={`${testPrefix}-${g.id}`} />
            {g.name}
          </label>
        ))}
      </div>
    </div>
  );
}

function SizesEditor({ sizes, onChange }) {
  const upd = (i, k, v) => onChange(sizes.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div data-testid="product-sizes-editor">
      <L>Sizes (optional — e.g. Half, Full, Small, Medium, Large)</L>
      <div className="space-y-2">
        {sizes.map((s, i) => (
          <div key={i} className="flex gap-2">
            <Input required placeholder="Size name" value={s.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`product-size-name-${i}`} />
            <Input required type="number" min="0" step="0.01" placeholder="Price ₹" className="w-32" value={s.price} onChange={(e) => upd(i, "price", e.target.value)} data-testid={`product-size-price-${i}`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => onChange(sizes.filter((_, j) => j !== i))} data-testid={`product-size-remove-${i}`}><X className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      {sizes.length < 10 && <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => onChange([...sizes, { name: "", price: "" }])} data-testid="product-size-add-btn"><Plus className="mr-1 h-4 w-4" />Add size</Button>}
      {sizes.length > 0 && <p className="mt-1 text-xs text-slate-400">Customers must pick a size; its price replaces the item price.</p>}
    </div>
  );
}

export function ProductForm({ product, lookups, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", description: "", image_url: "", category_id: lookups.categories[0]?.id || "",
    base_price: "", sale_price: "", is_available: true, sku: "", addon_group_ids: [], variation_group_ids: [], sizes: [],
    tax_type: "inherit", tax_rate: "", is_active: true, ...product,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...f,
      base_price: Number(f.base_price),
      sale_price: f.sale_price === "" || f.sale_price == null ? null : Number(f.sale_price),
      tax_rate: f.tax_rate === "" || f.tax_rate == null ? null : Number(f.tax_rate),
      sizes: (f.sizes || []).map((s) => ({ ...s, price: Number(s.price) })),
    };
    try {
      if (product.id) await api.patch(`/restaurant/products/${product.id}`, payload);
      else await api.post("/restaurant/products", payload);
      toast.success("Item saved");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{product.id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <ImageUpload value={f.image_url} onChange={set("image_url")} label="Photo" testId="product-image" />
          <div><L>Name</L><Input required value={f.name} onChange={set("name")} data-testid="product-name-input" /></div>
          <div><L>Description</L><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="product-desc-input" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><L>Category</L>
              <select required className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm" value={f.category_id} onChange={set("category_id")} data-testid="product-category-select">
                {lookups.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><L>SKU (optional)</L><Input value={f.sku} onChange={set("sku")} data-testid="product-sku-input" /></div>
            <div><L>Price (₹)</L><Input required type="number" min="0" step="0.01" value={f.base_price} onChange={set("base_price")} data-testid="product-price-input" /></div>
            <div><L>Sale price (₹, optional)</L><Input type="number" min="0" step="0.01" value={f.sale_price ?? ""} onChange={set("sale_price")} data-testid="product-sale-input" /></div>
          </div>
          <SizesEditor sizes={f.sizes || []} onChange={set("sizes")} />
          <GroupPicker title="Variations" groups={lookups.variations} selected={f.variation_group_ids} onChange={set("variation_group_ids")} testPrefix="product-variation" />
          <GroupPicker title="Add-ons" groups={lookups.addons} selected={f.addon_group_ids} onChange={set("addon_group_ids")} testPrefix="product-addon" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div><L>GST rule</L>
              <select className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm" value={f.tax_type} onChange={set("tax_type")} data-testid="product-tax-type">
                <option value="inherit">Use restaurant default</option>
                <option value="inclusive">Price includes GST</option>
                <option value="exclusive">GST added on top</option>
              </select>
            </div>
            {f.tax_type !== "inherit" && (
              <div><L>GST rate % (blank = default)</L><Input type="number" min="0" max="100" step="0.01" value={f.tax_rate ?? ""} onChange={set("tax_rate")} data-testid="product-tax-rate" /></div>
            )}
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_available} onCheckedChange={set("is_available")} data-testid="product-available-switch" />Available</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_active} onCheckedChange={set("is_active")} data-testid="product-active-switch" />Show on menu</label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid="product-save-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
